import { Icon } from '@mui/material';
import Button from 'app/shared-components/marketing/Button';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { fetchTestimonials, selectTestimonials } from 'app/store/marketing/testimonialsSlice';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import TestimonialPreview from 'app/shared-components/marketing/TestimonialPreview';
import PreviewSlider from 'app/shared-components/marketing/PreviewSlider';
import { useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import _ from '@lodash';
import { apiGet, apiPut, apiDel, apiPost } from 'app/shared-components/util/restAPI';
import { showMessage } from 'app/store/fuse/messageSlice';
import {  startLoading, stopLoading} from 'app/store/utilSlice'; 
import { errorMessage } from'app/shared-components/util/message';
import LoadingBackdrop from 'app/shared-components/layout/LoadingBackdrop';
import ControlledTextField from 'app/shared-components/form/ControlledTextField';
import ControlledCheckbox from 'app/shared-components/form/ControlledCheckbox';
import ControlledDatePicker from 'app/shared-components/form/ControlledDatePicker';
import DefaultButton from 'app/shared-components/layout/DefaultButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { motion } from 'framer-motion';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import TextBubble from 'app/shared-components/marketing/TextBubble';
import HeaderTags from 'app/shared-components/helmet/HeaderTags';
import PlanPricingCards from "app/shared-components/account/PlanPricingCards";

const features = [
  {
    id: 0,
    title: 'Communicate with Providers',
    body: 'End the frustration of trying to coordinate multiple care providers through text, email, and phone. Our HIPAA-compliant platform allows providers to share details, answer and ask questions of patients and family members to reduce patient/family stress.',
    icon: 'chat'
  },
  {
    id: 1,
    title: 'Find Relevant Care',
    body: 'Finding the right provider or program can be time consuming and frustrating. Halespring can help find the mental health care that works best for you, including outpatient providers, group therapy and higher leve-of-care programs (such as Intensive Outpatient or Residential programs)',
    icon: 'search'
  },
  {
    id: 2,
    title: 'Private and Secure',
    body: "Your personal information will not be disclosed unless you decide to. Anything you share with your providers is protected and covered by HIPAA. And we don't sell your data, ever.",
    icon: 'lock'
  },
  {
    id: 3,
    title: 'Document Sharing and Storage',
    body: 'Your providers can provide better care when they have access to patient documentation. Halespring is a great way to securely share information and documents.',
    icon: 'share'
  },
]

function getFeatureBgClasses(i) {
  switch (i % 4) {
    case 0: return { bg: 'bg-hs-blue-pale', icon: 'bg-hs-blue-light' };
    case 2: return { bg: 'bg-hs-green-light', icon: 'bg-hs-green' };
    default: return { bg: 'bg-hs-grey-light', icon: 'bg-hs-grey' }
  }
}


const LAUNCH_SUBSCRIPTION_NAME= "Launch Updates";
const SUPPORT_SUBSCRIPTION_NAME= "News and Offers";
const LEGAL_BASIS_INTERESTED_LEAD= "LEGITIMATE_INTEREST_PQL";


/**
 * Form Validation Schema
 */
const schema = yup.object().shape({
  email: yup.string().email('You must enter a valid email').required('You must enter a email'),
  firstName: yup.string().required('Please enter your first name'),
}, []);

const defaultValues = {
  email: '',
  firstName: '',
};


export default function Patients() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const email= searchParams.get("email");
  const initialValues =  email ? { email: email } : undefined;
  const signupRef = useRef(null);
  
  const { control, formState, handleSubmit, setError, reset } = useForm({
    mode: 'onChange',
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { isValid, dirtyFields, errors } = formState;
  
  useEffect(() => {
    if (initialValues) {
        reset( initialValues );
    }
  }, [initialValues]);
  
  
  function onSubmit({ email, firstName }) {
	   dispatch(startLoading());
	   const apiName = 'PublicAPI2';
	   const path = '/x2/emailsubscription/public/create';
	   const interests= ["patients"];
	   const subscriptionNames= [SUPPORT_SUBSCRIPTION_NAME];

	   const options = {
			  body: { type: "nonProvider",
			          interests: interests,
			          firstName: firstName,
			          subscriptionNames: subscriptionNames,
			          legalBasis: LEGAL_BASIS_INTERESTED_LEAD,
			          legalBasisExplanation: "User provided email and opted-in to communications.",
			          email: email},
			  headers: {},
			  response: false, // OPTIONAL (return the entire Axios response object instead of only response.data)
		 };

  	  apiPut(apiName, path, options)
		    .then(resp => setSubmitted(true))
		    .catch(error => errorMessage(dispatch, error.message))
		    .finally(() => dispatch(stopLoading()));
  }

  useEffect(() => {
    dispatch(fetchTestimonials({
      pagination: {
        page: 1,
        pageSize: 3
      },
      sort: ['publishedAt:desc']
    }));
  }, [])

  const testimonials = useSelector(selectTestimonials());

  const testimonialSlider = useRef(null);
  
  const host = window.location.protocol + '//' + window.location.host;
  const orgImage=  host + '/assets/images/logo/HALESPRING-HEADER-191.jpg';

  return (
    <>
      <HeaderTags title="Halespring for Patients And Families"
                  description="An online community for the mental health ecosystem, offering essential tools for professionals, patients, and loved ones." 
                  keywords="Mental Health,Therapist,Therapy,Community,Referrals,Pyschologist,Psychiatrist"
                  image={orgImage}
      />
    
    <div className="pt-48">
      {/* Hero */}
      <section>
        <div className="container">
          <h1 className="h3 lg:h1 text-center">
            Collaborate Confidently. Create Community. Improve Outcomes.
          </h1>

          <p className="body lg:body-lg text-center mt-12 lg:mt-20">
            Halespring is a secure and private, HIPAA-compliant platform for mental healthcare providers, patients and their families.
            We’re here to improve the way mental healthcare is delivered by enabling better communication with and between your care providers,
            as well as provide support and resources to help your journey.
          </p>

          <div className="flex justify-center mt-12 lg:mt-40">
            <Button to="/user-sign-up" className="bg-hs-blue text-hs-white">
              Get Started Now
            </Button>
          </div>
        </div>

        <div className="relative mt-20">
          <div className="bg-hs-white absolute bottom-0 left-0 w-full h-1/2 z-0" />

          <div className="container">
            <div className="grid grid-cols-2 lg:grid-cols-4 lg:grid-rows-2 lg:grid-flow-col items-center gap-20 relative z-10">
              {/* <img className="w-full hidden lg:block lg:order-0" src="/assets/images/marketing/providers/early-access.svg" /> */}
              <TextBubble
                className="w-full hidden lg:block lg:order-0"
                shape="onePoint"
                label={['Communicate', 'With Providers']}
                shapeClass="rotate-180"
              />
              {/* <img className="w-full order-1 lg:order-1" src="/assets/images/marketing/providers/request.svg" /> */}
              <TextBubble
                className="w-full order-1 lg:order-1"
                shape="twoPoints"
                label={['Peer Support', 'Groups']}
                shapeClass="fill-hs-green"
              />
              <img className="w-full col-span-2 row-span-2 rounded-20 order-0 lg:order-2" src="/assets/images/marketing/patients/family.jpg" />
              {/* <img className="w-full hidden lg:block lg:order-3" src="/assets/images/marketing/providers/reserve.svg" /> */}
              <TextBubble
                className="w-full hidden lg:block lg:order-3"
                shape="twoPoints"
                label={['Find The', 'Right Care']}
                shapeClass="fill-hs-green"
              />
              {/* <img className="w-full order-2 lg:order-4" src="/assets/images/marketing/providers/pros.svg" /> */}
              <TextBubble
                className="w-full order-2 lg:order-4"
                shape="onePoint"
                label={['Private', 'And Secure']}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-hs-white">
        <div className="container py-40">
          <h2 className="h3 lg:h2 text-center uppercase">
            Our Features
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-40">
            {features.map((feature, i) => {
              const bgClasses = getFeatureBgClasses(i);

              return (
                <article
                  key={feature.id}
                  className={classNames([
                    'p-20 lg:p-40 pb-72 lg:pb-120 rounded-20',
                    bgClasses.bg
                  ])}
                >
                  <div className="flex justify-between items-start">
                    <h3 className="h4 lg:h2 wrap-break-word min-w-0">
                      {feature.title}
                    </h3>

                    <div className={classNames([
                      'w-60 h-60 lg:w-80 lg:h-80 flex justify-center items-center rounded-full text-hs-white flex-none ml-20',
                      bgClasses.icon
                    ])}>
                      <Icon>{feature.icon}</Icon>
                    </div>
                  </div>

                  <p className="body-md lg:body-lg mt-28">
                    {feature.body}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sign Up */}
      <section className="relative bg-hs-blue-pale">
        <div className="absolute z-0 top-0 left-0 w-full h-1/2 bg-hs-white" />

        <div className="container pt-40 pb-60">
          <div className="relative z-10 bg-hs-blue lg:rounded-20 text-center px-36 py-60 lg:pt-92 lg:pb-68 ">
            <h2 className="h2 uppercase text-hs-white text-center">
              Join Halespring Today
            </h2>

            <p className="body mt-20 lg:mt-30 text-hs-white ">
              Sign up now to join Halespring and take control of your mental health care!
            </p>

            <div className="mt-20 lg:mt-60">
              <Button
                  to="/user-sign-up"
                  className="bg-hs-green text-hs-white"
              >
                Sign Up Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="bg-hs-blue-pale pt-60">
        <div className="container">
          <h2 className="h3 lg:h2 text-center uppercase">
            Our Pricing
          </h2>
          <PlanPricingCards type='user' isPublic/>
        </div>
      </section>
      

    {/*  /!* Testimonials *!/*/}
    {/*  <section className="bg-hs-blue-pale py-80">*/}
    {/*    <div className="container">*/}
    {/*      <PreviewSlider*/}
    {/*        title="How we Help"*/}
    {/*      >*/}
    {/*        {testimonials.map((testimonial) =>*/}
    {/*          <TestimonialPreview*/}
    {/*            key={testimonial.id}*/}
    {/*            testimonial={testimonial}*/}
    {/*          />*/}
    {/*        )}*/}
    {/*      </PreviewSlider>*/}
    {/*    </div>*/}
    {/*  </section>*/}

    </div>
    </>
  );
}